<template>
  <BaseLocationQuickSearch
    :id="id"
    ref="baseLocationSearchRef"
    v-model="internalValue"
    :search-field="searchField"
    :locations="locations"
    :suggestions="groupedResults"
    :append-to="appendTo"
    :errors="errors"
    :placeholder="placeholder"
    :disabled="disabled"
    :autofocus="autofocus"
    :option-label="autocompleteOptionLabel"
    option-group-label="label"
    option-group-children="items"
    @blur="onAutoCompleteBlur"
    @clear="emit('clear')"
    @search="onLocationSearch"
    @select="emit('select', $event)"
  >
    <template #option="{ option }">
      <LocationQuickSearchOption :option="option" />
    </template>
  </BaseLocationQuickSearch>
</template>

<script lang="ts" setup>
import { ErrorObject } from '@vuelidate/core';
import { CancelTokenSource, default as axios } from 'axios';
import {
  computed,
  PropType,
  ref,
  Ref,
  toRefs,
  useAttrs,
  useTemplateRef,
} from 'vue';
import { useI18n } from 'vue-i18n';

import { useModelValue, useToast } from '@rygen/rygen-ui/composables';
import {
  Loadable,
  Location,
  LocationTypeWellKnownNaturalId,
  ShippingPointType,
} from '@rygen/rygen-ui/model';

import api from '@/api';

import BaseLocationQuickSearch, {
  LocationSearchField,
} from '@/components/location/BaseLocationQuickSearch.vue';
import LocationQuickSearchOption from '@/components/location/LocationQuickSearchOption.vue';

export type LocationGroup = {
  label: string;
  items: Location[];
};

export type SupportedLocationType =
  | LocationTypeWellKnownNaturalId.SHIPPING_POINT
  | LocationTypeWellKnownNaturalId.BILL_TO;

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  modelValue: {
    type: Location,
    default: () => new Location(),
  },
  searchField: {
    type: String as PropType<LocationSearchField>,
    default: () => 'name',
  },
  type: {
    type: String as PropType<SupportedLocationType>,
    default: () => LocationTypeWellKnownNaturalId.SHIPPING_POINT,
  },
  shippingPointTypes: {
    type: Array as PropType<ShippingPointType[]>,
    default: () => [],
  },
  errors: {
    type: Array as PropType<ErrorObject[]>,
    default: () => [],
  },
  appendTo: {
    type: String as PropType<'body' | 'self'>,
    default: () => 'self',
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
  autofocus: {
    type: Boolean,
    default: () => false,
  },
  includeAddressBookResults: {
    type: Boolean,
    default: () => true,
  },
  includeTrimbleResults: {
    type: Boolean,
    default: () => false,
  },
});
const {
  id,
  modelValue,
  searchField,
  type,
  shippingPointTypes,
  errors,
  appendTo,
  disabled,
  autofocus,
  includeAddressBookResults,
  includeTrimbleResults,
} = toRefs(props);
const emit = defineEmits<{
  (event: 'clear'): void;
  (event: 'select', newVal: Location): void;
  (event: 'update:model-value', newVal: Location): void;
}>();

const baseLocationSearchRef = useTemplateRef('baseLocationSearchRef');

const attrs = useAttrs();
const { internalValue } = useModelValue(modelValue, {
  emit,
  factoryMethod: (opts) => new Location(opts),
});
const { t } = useI18n();
const toast = useToast();

const locations: Ref<Loadable<Location[]>> = ref({ loading: false });
const cancelRequest: Ref<CancelTokenSource | undefined> = ref(undefined);

const placeholder = computed(() => {
  const placeholderAttribute = attrs.placeholder as string | undefined;
  return (
    placeholderAttribute ??
    t('common.paramName', { param: t('locations.locations') })
  );
});

const groupedResults: Ref<LocationGroup[]> = computed(() => {
  const results = locations.value.data ?? [];

  // Group the locations: address book (with IDs) vs Trimble (without IDs)
  const addressBookLocations = results.filter(isAddressBookLocation);
  const trimbleLocations = results.filter((loc) => !isAddressBookLocation(loc));

  const groups = [];

  if (addressBookLocations.length > 0) {
    groups.push({
      label: t('address.addressBook'),
      items: addressBookLocations,
    });
  }

  if (trimbleLocations.length > 0) {
    groups.push({
      label: t('address.addresses'),
      items: trimbleLocations,
    });
  }

  return groups;
});

const autocompleteOptionLabel = (opt: Location | string) => {
  if (typeof opt === 'string') {
    return opt;
  }
  // Use name for address book locations, addressLine1 for Trimble results
  const label = isAddressBookLocation(opt)
    ? opt.name
    : opt.address?.addressLine1;
  return label ?? '';
};

const isAddressBookLocation = (location: Location): boolean => {
  // Address book locations have IDs, Trimble results don't
  return location.id != null;
};

const onLocationSearch = async (input: string) => {
  cancelRequest.value?.cancel();
  cancelRequest.value = axios.CancelToken.source();

  try {
    locations.value.loading = true;
    locations.value.data =
      type.value === LocationTypeWellKnownNaturalId.SHIPPING_POINT
        ? await api.shippingPoint.quickSearch(input, {
            shippingPointTypes: shippingPointTypes.value,
            includeAddressBook: includeAddressBookResults.value,
            includeTrimble: includeTrimbleResults.value,
            cancelToken: cancelRequest.value,
          })
        : await api.billTo.quickSearch(input, {
            cancelToken: cancelRequest.value,
          });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    locations.value.error = e;
    console.error('Error loading locations', e);
    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('locations.locations', 2),
      }),
      error: e,
    });
  } finally {
    locations.value.loading = false;
  }
};

const onAutoCompleteBlur = () => {
  // cancel ongoing http request
  cancelRequest?.value?.cancel();
  baseLocationSearchRef.value?.clearSearchTimeout();
};
</script>
